import dayjs from 'dayjs';

import type { BedCategory } from 'app/typings';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type {
  BookingDetails,
  BookingExperienceResponse,
  BookingRoomResponse,
} from 'app/typings/core/extranet/bookings/booking';
import { pluralize } from 'app/utils/strings';

import type { InfoRow } from '../../../components/commons/InfoBlock/InfoBlock';

export type PriceRow = {
  label: string;
  price: number;
  subRows?: PriceRow[];
  isDateIndicator?: boolean;
  isNightCount?: boolean;
  isOriginals?: boolean;
};

export type ExtraRow = PriceRow & {
  unitPrice: number;
  showUnitPrice: boolean;
  isOriginals: boolean;
};

const formatDate = (dateStr: string): string =>
  dayjs(dateStr).format('DD/MM/YYYY');

const isWithChildMode = (exp: BookingExperienceResponse): boolean =>
  exp.priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
  exp.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME;

const groupOpeningsByPrice = (
  exp: BookingExperienceResponse
): { adultPrice: number; childPrice: number; count: number }[] => {
  const groups = exp.openings.reduce((acc, opening) => {
    const adultPrice = opening.price ?? 0;
    const childPrice = opening.childPrice ?? 0;
    const key = `${adultPrice}:${childPrice}`;
    const existing = acc.get(key);

    if (existing) {
      existing.count += 1;
    } else {
      acc.set(key, { adultPrice, childPrice, count: 1 });
    }

    return acc;
  }, new Map<string, { adultPrice: number; childPrice: number; count: number }>());

  return Array.from(groups.values());
};

const areAllPaxPricesEqual = (
  openings: BookingExperienceResponse['openings']
): boolean => {
  if (openings.length === 0) {
    return true;
  }

  const firstAdultPrice = openings[0].price ?? 0;
  const firstEffectiveChildPrice = openings[0].childPrice ?? firstAdultPrice;

  return (
    firstAdultPrice === firstEffectiveChildPrice &&
    openings.every(
      (o) =>
        (o.price ?? 0) === firstAdultPrice &&
        (o.childPrice ?? o.price ?? 0) === firstAdultPrice
    )
  );
};

const getChargedAdults = (
  adults: number,
  children: number,
  minChargedPax?: number | null
): number => adults + Math.max(0, (minChargedPax ?? 0) - (adults + children));

const getIncludedExperienceBookedStock = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  minChargedPax?: number | null
): number => {
  const numOpenings = exp.openings.length;

  if (exp.isPerPerson) {
    const chargedAdults = getChargedAdults(adults, children, minChargedPax);

    return (
      exp.quantity *
      (chargedAdults + children) *
      (exp.isPerNight ? numOpenings : 1)
    );
  }

  return exp.quantity * (exp.isPerNight ? numOpenings : 1);
};

export const getExperienceTotal = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  minChargedPax?: number | null
): number => {
  const discount = 1 - (exp.packDiscount ?? 0);

  if (exp.isPerPerson && (adults > 0 || children > 0)) {
    const chargedAdults = getChargedAdults(adults, children, minChargedPax);

    return (
      exp.openings.reduce((sum, o) => {
        const adultPrice = o.price ?? 0;
        const childPrice = o.childPrice ?? 0;

        return sum + chargedAdults * adultPrice + children * childPrice;
      }, 0) * discount
    );
  }

  const quantity = exp.openings[0]?.bookedStock ?? 0;

  return (
    quantity *
    exp.openings.reduce((sum, o) => sum + (o.price ?? 0), 0) *
    discount
  );
};

const buildPaxSubRows = (
  adults: number,
  children: number,
  adultPrice: number,
  childPrice: number,
  currencyFormatter: (price?: number | undefined) => string,
  numNights = 1
): PriceRow[] => [
  ...(adults > 0 && adultPrice > 0
    ? [
        {
          label: `${adults * numNights}x Adult price (${currencyFormatter(
            adultPrice
          )})`,
          price: adults * numNights * adultPrice,
        },
      ]
    : []),
  ...(children > 0 && childPrice > 0
    ? [
        {
          label: `${children * numNights}x Child price (${currencyFormatter(
            childPrice
          )})`,
          price: children * numNights * childPrice,
        },
      ]
    : []),
];

const getPerNightPaxSubRows = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  currencyFormatter: (price?: number | undefined) => string,
  minChargedPax?: number | null
): PriceRow[] => {
  const discount = 1 - (exp.packDiscount ?? 0);
  const effectiveAdults = exp.isPerPerson
    ? getChargedAdults(adults, children, minChargedPax)
    : adults;

  return groupOpeningsByPrice(exp).flatMap(
    ({ adultPrice, childPrice, count }) => {
      const discountedAdultPrice = adultPrice * discount;
      const discountedChildPrice =
        childPrice !== undefined && childPrice > 0
          ? childPrice * discount
          : children > 0
          ? discountedAdultPrice
          : 0;

      return [
        ...buildPaxSubRows(
          effectiveAdults,
          children,
          discountedAdultPrice,
          discountedChildPrice,
          currencyFormatter,
          count
        ),
        {
          label: `${pluralize(count, 'night', 'nights')} at this price`,
          price: 0,
          isNightCount: true,
          isOriginals: !!exp.countryId,
        },
      ];
    }
  );
};

const getPerOncePaxSubRows = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  currencyFormatter: (price?: number | undefined) => string,
  minChargedPax?: number | null
): PriceRow[] => {
  const discount = 1 - (exp.packDiscount ?? 0);
  const effectiveAdults = exp.isPerPerson
    ? getChargedAdults(adults, children, minChargedPax)
    : adults;

  return exp.openings.flatMap((opening) => {
    const adultPrice = (opening.price ?? 0) * discount;
    const childPrice =
      opening.childPrice !== undefined
        ? (opening.childPrice ?? 0) * discount
        : children > 0
        ? adultPrice
        : 0;

    return [
      ...buildPaxSubRows(
        effectiveAdults,
        children,
        adultPrice,
        childPrice,
        currencyFormatter
      ),
      ...(exp.isOpeningDateManuallySelected
        ? [
            {
              label: `Planned on ${formatDate(opening.date)}`,
              price: 0,
              isDateIndicator: true,
              isOriginals: !!exp.countryId,
            },
          ]
        : []),
    ];
  });
};

export const getIncludedPerNightExperiences = (
  bookingDetails: BookingDetails
): BookingExperienceResponse[] =>
  bookingDetails.experiences.filter(
    (exp) => exp.isIncluded && (exp.isPerNight || exp.isAlwaysIncludedWithNight)
  );

export const getIncludedPerOnceExperiences = (
  bookingDetails: BookingDetails
): BookingExperienceResponse[] =>
  bookingDetails.experiences.filter(
    (exp) => exp.isIncluded && !exp.isPerNight && !exp.isAlwaysIncludedWithNight
  );

export const getFreeIncludedPerNightRow = (
  bookingDetails: BookingDetails
): PriceRow | null => {
  const { adults, children, minChargedPax } = bookingDetails;
  const freeExps = getIncludedPerNightExperiences(bookingDetails).filter(
    (exp) => getExperienceTotal(exp, adults, children, minChargedPax) === 0
  );

  if (freeExps.length === 0) {
    return null;
  }

  return {
    label: freeExps.map((exp) => exp.categoryName).join(' + '),
    price: 0,
  };
};

export const getPaidIncludedPerNightRows = (
  bookingDetails: BookingDetails,
  currencyFormatter: (price?: number | undefined) => string
): PriceRow[] => {
  const { adults, children, minChargedPax } = bookingDetails;

  return getIncludedPerNightExperiences(bookingDetails).flatMap((exp) => {
    const isOriginals = exp.isStaycationExperience;
    const price = isOriginals
      ? exp.quantity *
        exp.openings.reduce(
          (sum, o) => sum + (o.staycationPrice ?? o.price ?? 0),
          0
        )
      : getExperienceTotal(exp, adults, children, minChargedPax);

    if (price === 0) {
      return [];
    }

    const stock = getIncludedExperienceBookedStock(
      exp,
      adults,
      children,
      minChargedPax
    );
    const suffix = isOriginals
      ? ` (Originals • ${currencyFormatter(price)})`
      : '';

    return [
      {
        label: `${stock}x ${exp.categoryName}${suffix}`,
        price,
        subRows: !areAllPaxPricesEqual(exp.openings)
          ? getPerNightPaxSubRows(
              exp,
              adults,
              children,
              currencyFormatter,
              minChargedPax
            )
          : undefined,
        isOriginals,
      },
    ];
  });
};

export const getIncludedPerOnceRows = (
  bookingDetails: BookingDetails,
  currencyFormatter: (price?: number | undefined) => string
): PriceRow[] => {
  const { adults, children, minChargedPax } = bookingDetails;

  return getIncludedPerOnceExperiences(bookingDetails).map((exp) => {
    const isOriginals = exp.isStaycationExperience;
    const total = isOriginals
      ? exp.quantity *
        exp.openings.reduce(
          (sum, o) => sum + (o.staycationPrice ?? o.price ?? 0),
          0
        )
      : getExperienceTotal(exp, adults, children, minChargedPax);

    const bookedStock = getIncludedExperienceBookedStock(
      exp,
      adults,
      children,
      minChargedPax
    );
    const unitPrice =
      (exp.openings[0]?.price ?? 0) * (1 - (exp.packDiscount ?? 0));
    const label = isOriginals
      ? `${bookedStock}x ${exp.categoryName} (Originals • ${currencyFormatter(
          total
        )})`
      : exp.isPerPerson && !areAllPaxPricesEqual(exp.openings)
      ? `${bookedStock}x ${exp.categoryName}`
      : `${bookedStock}x ${exp.categoryName} (${currencyFormatter(unitPrice)})`;

    return {
      label,
      price: total,
      subRows:
        exp.isPerPerson && !areAllPaxPricesEqual(exp.openings)
          ? getPerOncePaxSubRows(
              exp,
              adults,
              children,
              currencyFormatter,
              minChargedPax
            )
          : undefined,
      isOriginals,
    };
  });
};

export const getExtraRows = (
  bookingDetails: BookingDetails,
  currencyFormatter: (price?: number | undefined) => string
): ExtraRow[] => {
  const { adults, children } = bookingDetails;

  return bookingDetails.experiences.reduce<ExtraRow[]>((acc, exp) => {
    if (exp.isIncluded) {
      return acc;
    }

    const isOriginals = exp.isStaycationExperience;

    const total = isOriginals
      ? exp.quantity *
        exp.openings.reduce(
          (sum, o) => sum + (o.staycationPrice ?? o.price ?? 0),
          0
        )
      : getExperienceTotal(exp, adults, children);

    const unitPrice = isOriginals
      ? exp.openings[0]?.staycationPrice ?? exp.openings[0]?.price ?? 0
      : exp.openings[0]?.price ?? 0;

    const bookedStock = exp.isPerPerson
      ? getIncludedExperienceBookedStock(exp, adults, children)
      : exp.openings[0]?.bookedStock;

    if (!isOriginals && isWithChildMode(exp)) {
      const bookedStockCalculed = (adults + children) * exp.openings.length;

      acc.push({
        label: `${bookedStockCalculed}x ${exp.categoryName}`,
        unitPrice,
        price: total,
        showUnitPrice: false,
        subRows: getPerOncePaxSubRows(exp, adults, children, currencyFormatter),
        isOriginals: false,
      });
    } else {
      acc.push({
        label: `${bookedStock}x ${exp.categoryName}`,
        unitPrice,
        price: total,
        showUnitPrice: true,
        subRows: undefined,
        isOriginals,
      });
    }

    return acc;
  }, []);
};

export const getRoomTotal = (room: BookingRoomResponse): number =>
  room.openings.reduce((sum, o) => sum + (o.discountPrice ?? 0), 0);

const getAdditionalBedTotal = (
  beds: BookingRoomResponse['beds'],
  numNights: number
): number =>
  beds?.reduce(
    (sum, bed) =>
      sum + (bed.isIncluded || !bed.isSelected ? 0 : bed.price * numNights),
    0
  ) ?? 0;

const groupBedsByCategory = (
  beds: BookingRoomResponse['beds'],
  bedCategories: BedCategory[],
  numNights: number
): Map<string, { price: number; count: number }> => {
  const grouped = new Map<string, { price: number; count: number }>();

  beds?.forEach((bed) => {
    if (bed.isIncluded) {
      return;
    }

    const category = bedCategories.find((c) => c.id === bed.categoryId);
    const categoryName = category?.name || 'Unknown';
    const existing = grouped.get(categoryName);

    if (existing) {
      existing.count += 1;
    } else {
      grouped.set(categoryName, { price: bed.price, count: numNights });
    }
  });

  return grouped;
};

export const getRoomRows = (
  bookingDetails: BookingDetails,
  bedCategories: BedCategory[]
): PriceRow[] =>
  bookingDetails.rooms.flatMap((room) => {
    const nonIncludedBeds =
      room.beds?.filter((bed) => !bed.isIncluded && bed.isSelected === true) ??
      [];

    const numNights = dayjs(bookingDetails.checkOut).diff(
      bookingDetails.checkIn,
      'day'
    );
    const additionalBedTotal = getAdditionalBedTotal(
      nonIncludedBeds,
      numNights
    );

    const rows: PriceRow[] = [
      {
        label: room.name ?? room.categoryName,
        price: getRoomTotal(room),
      },
    ];

    if (nonIncludedBeds.length > 0 && numNights > 0) {
      const bedsByCategory = groupBedsByCategory(
        nonIncludedBeds,
        bedCategories,
        numNights
      );
      const subRows: PriceRow[] = Array.from(bedsByCategory.entries()).map(
        ([categoryName, { price, count }]) => ({
          label: `${count}x ${categoryName} (${price}€)`,
          price: count * price,
        })
      );

      rows.push({
        label: `${numNights}x Fee for additional bed in room`,
        price: additionalBedTotal,
        subRows,
      });
    }

    return rows;
  });

export const mapPriceSubRows = (
  subRows: {
    label: string;
    price: number;
    isDateIndicator?: boolean;
    isNightCount?: boolean;
  }[],
  currencyFormatter: (price?: number | undefined) => string
): InfoRow[] =>
  subRows.map((s) => {
    if (s.isDateIndicator) {
      return { label: s.label, variant: 'date' as const };
    }

    if (s.isNightCount) {
      return { label: s.label, variant: 'subheader' as const };
    }

    return { label: s.label, value: currencyFormatter(s.price) };
  });
